// Users Frontend Functions Library
// These functions are self-contained and use _shared helpers directly (like Hoomie)

import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { UserModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';
import { getDoc, doc } from 'firebase/firestore';
import { getAuth, getAdditionalUserInfo, onAuthStateChanged } from 'firebase/auth';

const COLLECTION_NAME = 'users';

// Frontend function: Get all users
async function getUsers(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME);
    const users = docs.map((doc) => {
      return { id: doc.$id, model: new UserModel(doc) };
    });
    return createCMMSView({ data: users });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get user by ID
async function getUserById(db, userId) {
  try {
    const doc = await getDocumentById(db, COLLECTION_NAME, userId);
    if (!doc) return createCMMSView({ error: 'User not found' });
    const user = new UserModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: user } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get user by email
async function getUserByEmail(db, email) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'email',
      operator: '==',
      value: email,
    });

    if (docs.length === 0) {
      return createCMMSView({ error: 'User not found' });
    }

    const user = docs[0];
    return createCMMSView({
      data: { id: user.$id, model: new UserModel(user) },
    });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get user by Firebase Auth UID
async function getUserByUid(db, uid) {
  try {
    // Since we store the Firebase UID as the document ID, we can get it directly
    const userDoc = await getDoc(doc(db, COLLECTION_NAME, uid));
    
    if (!userDoc.exists()) {
      return createCMMSView({ error: 'User not found' });
    }

    const userData = userDoc.data();
    return createCMMSView({
      data: { id: userDoc.id, model: new UserModel(userData) },
    });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get users by company
async function getUsersByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    const users = docs.map((doc) => {
      return { id: doc.$id, model: new UserModel(doc) };
    });

    return createCMMSView({ data: users });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get users by company with Auth data
async function getUsersByCompanyWithAuth(db, companyId) {
  try {
    // Get Firestore users
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    // Get Firebase Auth users data
    const auth = getAuth(db.app);
    
    // For each Firestore user, try to get their Auth data
    const usersWithAuth = await Promise.all(
      docs.map(async (doc) => {
        const userModel = new UserModel(doc);
        let authData = null;
        
        try {
          // Try to get the user from Firebase Auth
          
          // Since we can't directly get user by UID from client SDK,
          // we'll need to use a different approach
          // For now, we'll return the user without auth data
          // and handle this in the server-side function
          authData = {
            lastSignInTime: null,
            disabled: false,
            emailVerified: false
          };
        } catch (authError) {
          console.warn('Could not fetch auth data for user:', doc.$id, authError.message);
          authData = {
            lastSignInTime: null,
            disabled: false,
            emailVerified: false
          };
        }

        return { 
          id: doc.$id, 
          model: userModel,
          authData: authData
        };
      })
    );

    return createCMMSView({ data: usersWithAuth });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create new user
async function createUser(db, userData) {
  try {
    const user = new UserModel(userData);
    const docRef = await addDocToCollection(db, COLLECTION_NAME, user.toJSON());

    return createCMMSView({ data: docRef });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create user with Firebase Auth
async function createUserWithAuth(db, userData) {
  try {
    // Import the server function dynamically
    const { createUserWithAuth } = await import('./node/index.js');
    const result = await createUserWithAuth(db, userData);
    
    return createCMMSView({ 
      data: result.success ? result.data : null, 
      error: result.success ? null : result.error 
    });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Update user
async function updateUser(db, userId, updateData) {
  try {
    // First get the existing user data
    const existingUser = await getDocumentById(db, COLLECTION_NAME, userId);
    if (!existingUser) {
      return createCMMSView({ error: 'User not found' });
    }

    // Merge existing data with update data
    const mergedData = {
      ...existingUser,
      ...updateData,
      updated_at: Date.now()
    };

    const user = new UserModel(mergedData);
    const docRef = await setDocById(db, COLLECTION_NAME, userId, user.toJSON());

    return createCMMSView({ data: { id: userId, ...user.toJSON() } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete user
async function deleteUser(db, userId) {
  try {
    // Import the server function dynamically
    const { deleteUser: deleteUserNode } = await import('./node/index.js');
    const result = await deleteUserNode(db, userId);
    
    return createCMMSView({ 
      data: result.success ? { id: userId, deleted: true } : null, 
      error: result.success ? null : result.error 
    });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get users by role
async function getUsersByRole(db, role) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'role',
      operator: '==',
      value: role,
    });

    const users = docs.map((doc) => {
      return { id: doc.$id, model: new UserModel(doc) };
    });

    return createCMMSView({ data: users });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get technicians
async function getTechnicians(db) {
  return await getUsersByRole(db, 'technician');
}

// Frontend function: Get supervisors
async function getSupervisors(db) {
  return await getUsersByRole(db, 'supervisor');
}

// Frontend function: Get managers
async function getManagers(db) {
  return await getUsersByRole(db, 'manager');
}

// Frontend function: Get admins
async function getAdmins(db) {
  return await getUsersByRole(db, 'admin');
}

// Export frontend functions
export {
  getUsers,
  getUserById,
  getUserByEmail,
  getUserByUid,
  getUsersByCompany,
  createUser,
  createUserWithAuth,
  updateUser,
  deleteUser,
  getUsersByRole,
  getTechnicians,
  getSupervisors,
  getManagers,
  getAdmins,
};
